import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { I18nProvider } from "@lingui/react";
import "./polyfill";

import { NotAvailable } from "account/notavailable";
import { ThemeList, onThemeChange, applyAccountTheme } from "account/theme";
import { ConfigProvider } from "widgets/configprovider";

import "../../styles/index.override.css";

import { rtdbClient } from "@base/common/clients/rtdb";
import { pushNotificationClient } from "@base/common/clients/push_notification";
import {
  PORTAL_ROUTES,
  AuthorizedComponents,
  PublicRoute,
  PrivateRoute,
  PortalRoute,
  routes
} from "./routes";
import {
  i18n,
  loadLocale,
  LANGUAGE_CODE,
  parseMissingMessage
} from "@base/common/libs/i18n/index.js";
import { postMessageToServiceWorker } from "../../common/libs/utils/serviceworker";
import {
  SERVICE_WORKER_LISTENER_TYPES,
  EXTENDED_MANAGEMENT_MODEL_IDS
} from "../../common/constants";
import { getSandbox } from "@base/common/libs/utils";
import { useAccountStore, useUserStore } from "@base/common/libs/stores";
import { Theme } from "@base/common/libs/theme/index.js";

//to register the routes which are navigatable without login
export { PublicRoute, PrivateRoute, PortalRoute };

BaseComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
  history: PropTypes.any,
  topMenu: PropTypes.any
};

export function BaseComponent({ children, topMenu }) {
  let [isFailed, setFailStatus] = useState(false);
  // const [isAuthendicated, setReady] = useState(false);
  const language = useAccountStore((state) => state.Language);
  /* eslint-disable-next-line */
  const [activeLanguage, setActiveLanguage] = useState("English");
  const accountId = useAccountStore((state) => state._id);
  const isPortal = useAccountStore((state) => state.IsPortal || false);
  const accountTheme = useAccountStore((state) => state.AccountTheme);

  window.isRTL = language?.RTL;

  useEffect(function loadAccountData() {
    useAccountStore
      .getState()
      .load()
      .then(() => {
        if (useAccountStore.getState().UserDetails?.AccountDetails) {
          const accountDetails =
            useAccountStore.getState().UserDetails?.AccountDetails;
          const { Features } = accountDetails;
          const featureFlag = Object.keys(EXTENDED_MANAGEMENT_MODEL_IDS).find(
            (flag) => Features.includes(flag)
          );
          if (featureFlag) {
            useAccountStore.getState().setAccessDetails(true);
            useAccountStore
              .getState()
              .setModelId(EXTENDED_MANAGEMENT_MODEL_IDS[featureFlag]);
            useAccountStore
              .getState()
              .getExtendedUserAdmins(
                EXTENDED_MANAGEMENT_MODEL_IDS[featureFlag]
              );
          }
        }
        useUserStore
          .getState()
          .setAccount(
            useAccountStore.getState()._id,
            useAccountStore.getState()?.AppConfig
          );

        (() => {
          const platformData = JSON.parse(
            localStorage.getItem("Theme.Data") || "{}"
          );
          const theme = new Theme();
          theme.load(platformData);
          const style = document.createElement("style");
          style.setAttribute("id", "platformtheme");
          style.innerHTML = `body{ ${theme.toCSS()} }`;
          document.head.append(style);
        })();

        (() => {
          const appata = JSON.parse(
            localStorage.getItem("Theme.App.Data") || "{}"
          );
          const theme = new Theme();
          theme.load(appata);
          const style = document.createElement("style");
          style.setAttribute("id", "apptheme");
          style.innerHTML = `.appTheme-app { ${theme.toCSS()} }`;
          document.head.append(style);
        })();

        return useUserStore.getState().load();
      })
      .then(() => {
        let currentUser = useUserStore.getState().currentUser;
        let accountDetails = currentUser?.AccountDetails;

        if (accountDetails) {
          getSandbox(accountDetails);
        }

        if (currentUser?.AppConfig) {
          let firebaseConfig = currentUser.AppConfig.Firebase;
          let pushNotificationConfig =
            currentUser.AppConfig.PushNotification.web;
          if (firebaseConfig) {
            rtdbClient.init(useAccountStore.getState()._id, firebaseConfig);
          }
          if (pushNotificationConfig) {
            pushNotificationClient.init(
              useAccountStore.getState()._id,
              pushNotificationConfig
            );
          }
          // triggerUserSyncEvents();
          postMessageToServiceWorker({
            type: SERVICE_WORKER_LISTENER_TYPES.CHAT_SYNC_INTERVAL
          });
        }
      })
      .catch((e) => {
        setFailStatus(true);
      });
    // .then(() => {
    //   if (ANON_URLS.includes(window.location.pathname)) {
    //     window.location.href = "/";
    //   } else {
    //     setReady(true);
    //   }
    // })
    // .catch((error) => {
    //   console.log("ERROR", error);
    //   if (!ANON_URLS.includes(window.location.pathname)) {
    //     // window.location.href = "/login";
    //   } else {
    //     setReady(true);
    //   }
    // });
  }, []);

  useEffect(
    function changeLanguage() {
      if (!language) {
        return;
      }
      console.info("===> trying to load language ", language);
      let languageCode = LANGUAGE_CODE[language.Name];
      // let baseUrl = useAccountStore.getState().AppConfig?.ResourcesPath; // to be rename
      window.BUILD_NUMBER = BUILD_NUMBER;
      // let buildNumber = getCookie("__TRANS_NUMBER");
      let buildNumber = window.translationBuildNumber;

      if (language.RTL) {
        document.body.classList.add("rtl");
        document.querySelector("html").setAttribute("dir", "rtl");
        document.body.classList.add("ar");
      } else {
        document.body.classList.remove("rtl");
        document.querySelector("html").setAttribute("dir", "ltr");
        document.body.classList.remove("ar");
      }

      function onLoadLocale(language) {
        console.info("loaded module ", language);
        window.remoteLanguage = language || { default: {} };

        //in-local development each service will set the loadTranslation method from theri app.jsx
        let loadLocalTranslation =
          useAccountStore.getState().translationLoader ||
          function translationFallback() {
            return Promise.resolve({
              default: {}
            });
          };
        return loadLocalTranslation(languageCode);
        // return import(
        //   `../../resource/locales/${languageCode}/messages.js?nocache=true`
        // );
      }

      loadLocale(
        // eslint-disable-next-line max-len
        `${window.location.protocol}${window.publicPath}static/translation/web/${buildNumber}/${languageCode}/messages.js`
      )
        .then(onLoadLocale, onLoadLocale)
        .then((localLanguage) => {
          i18n.load({
            [languageCode]: Object.assign(
              {},
              localLanguage.default,
              window.remoteLanguage.default
            )
          });
          i18n.activate(languageCode);
          setActiveLanguage(language);
          i18n._missing = (language, messageId) => {
            return (interpolateMsg) => {
              return parseMissingMessage(messageId, interpolateMsg);
            };
          };
          console.warn("language changed ===> ", language, i18n._missing);
        })
        .catch((err) => {
          console.error("Failed to load translation ", err);
        });
    },
    [language]
  );

  useEffect(
    function applyingAccountTheme() {
      let theme = localStorage.getItem("Theme");
      onThemeChange(theme || ThemeList[1]._id);
      applyAccountTheme(accountTheme?.ColorCode, accountTheme?.ApplyBackground);
    },
    [accountTheme]
  );

  let childComponents = React.Children.toArray(children);
  let privateChildrens = childComponents.filter((comp) => {
    return comp.type === PrivateRoute;
  });
  let publicChildrens = childComponents.filter((comp) => {
    return comp.type === PublicRoute;
  });
  let portalChildrens = childComponents.filter((comp) => {
    return comp.type === PortalRoute;
  });

  if (isFailed) {
    return (
      <React.Fragment>
        <I18nProvider i18n={i18n}>
          <BrowserRouter basename="/view">
            <Switch>
              <Route component={NotAvailable} />
            </Switch>
          </BrowserRouter>
        </I18nProvider>
        ;
      </React.Fragment>
    );
  }

  if (accountId && isPortal) {
    return (
      <I18nProvider i18n={i18n}>
        <ConfigProvider direction={window.isRTL ? "rtl" : "ltr"}>
          <BrowserRouter basename="/portal">
            <Switch>
              {PORTAL_ROUTES.map(({ path, component, hasAuth, ...etc }, i) => {
                let Component = component;
                return (
                  <PortalRoute key={path} path={path} {...etc}>
                    <Component />
                  </PortalRoute>
                );
              })}

              <Route path="/">
                <AuthorizedComponents>{portalChildrens}</AuthorizedComponents>
              </Route>
            </Switch>
          </BrowserRouter>
        </ConfigProvider>
      </I18nProvider>
    );
  }

  return accountId ? (
    <I18nProvider i18n={i18n}>
      <ConfigProvider direction={window.isRTL ? "rtl" : "ltr"}>
        <BrowserRouter basename="/view">
          <Switch>
            {routes.map(({ path, component, hasAuth, ...etc }, i) => {
              let Component = component;
              return (
                <Route key={path} path={path} {...etc}>
                  <Component />
                </Route>
              );
            })}
            {publicChildrens}
            <Route path="/">
              <AuthorizedComponents>{privateChildrens}</AuthorizedComponents>
            </Route>
          </Switch>
        </BrowserRouter>
      </ConfigProvider>
    </I18nProvider>
  ) : (
    ""
  );
}
